// Extracted from CaseBuddy.vue in refactor pass 2.
// Auth/session helpers and Hermes agent bridge/range-read/write methods.
// These are Options API method definitions and intentionally use `this`.

import {
  buildCaseAddressColorCoverageAnalysis,
  buildCaseDepositHotWalletReviewAnalysis,
  buildCaseDepositPatternReviewAnalysis,
  buildCaseDuplicateHashLabelerAnalysis,
  buildCaseInconsistencyReviewAnalysis,
  buildCaseKnownEntityCasingReviewAnalysis,
  buildCaseNoteHashRewriteAnalysis,
  buildMainRawDataCoverage,
  buildPathOperationAnalysis,
  buildSaplingPreparationAnalysis,
  CASE_DELIVERABLE_GROUPS,
  CASE_DEPOSIT_PATTERN_REVIEW_STATUS_DISMISSED,
  CASE_DEPOSIT_PATTERN_REVIEW_STATUS_REVIEWED,
  CASE_ENTITY_KIND_EXCHANGE,
  CASE_ENTITY_KIND_NON_EXCHANGE,
  CASE_REPORT_BODY_VARIANT_STANDARD,
  createCaseSheetState,
  DEFAULT_AUTH_SESSION,
  findExactPathOperationCandidateForRow,
  findPathOperationCandidateForRow,
  formatCaseEntityKindLabel,
  normalizeAuthSession
} from '../CaseBuddyCore.js';
import AuthService from '../../services/AuthService';

export const caseBuddyAgentBridgeMethods = {
     async loadAuthSession() {
    this.authSessionLoaded = false;
    this.authSessionError = '';

    try {
      const session = await AuthService.getSession();
      this.authSessionRaw = session;
      this.authSession = normalizeAuthSession(session);
    } catch (error) {
      console.error('HermesSpreadsheetPage auth session load failed:', error);
      this.authSessionRaw = null;
      this.authSession = { ...DEFAULT_AUTH_SESSION };
      this.authSessionError = 'Unable to load account permissions.';
    } finally {
      this.authSessionLoaded = true;
    }
  },

    normalizeAgentRangeWriteData(rawValue) {
  const parsed = typeof rawValue === 'string' ? JSON.parse(rawValue) : rawValue;

  if (!Array.isArray(parsed) || parsed.length === 0) {
    throw new Error('Range data must be a non-empty JSON array.');
  }

  return parsed.map(row => {
    if (Array.isArray(row)) {
      return row.map(value => value == null ? '' : value);
    }
    return [row == null ? '' : row];
  });
},

buildRangeRefFromBounds(startRow, startCol, rowCount, colCount) {
  const endRow = startRow + rowCount - 1;
  const endCol = startCol + colCount - 1;

  const startRef = `${this.indexToColumnLabel(startCol)}${startRow + 1}`;
  const endRef = `${this.indexToColumnLabel(endCol)}${endRow + 1}`;

  return startRef === endRef ? startRef : `${startRef}:${endRef}`;
},

agentWriteRange() {
  this.agentReadback = '';

  const startCell = this.parseCellRef(this.agentWriteRangeStartRef);
  if (!startCell) {
    this.agentStatus = 'Invalid write range start cell reference.';
    return { ok: false, status: this.agentStatus };
  }

  let matrix;
  try {
    matrix = this.normalizeAgentRangeWriteData(this.agentWriteRangeData);
  } catch (error) {
    this.agentStatus = error?.message || 'Invalid range JSON.';
    return { ok: false, status: this.agentStatus };
  }

  const sheet = this.getInputSheet();
  const result = sheet?.setRangeInput?.(startCell.row, startCell.col, matrix, 'agent_panel');

  if (!result?.ok) {
    this.agentStatus = result?.status || `Failed to write range from ${startCell.ref}.`;
    return { ok: false, status: this.agentStatus };
  }

  const rangeRef = this.buildRangeRefFromBounds(
    startCell.row,
    startCell.col,
    result.rowCount,
    result.colCount
  );

  this.agentRangeRef = rangeRef;
  this.agentCellRef = startCell.ref;

  sheet?.selectCell?.(startCell.row, startCell.col);
  this.formulaTargetCell = { row: startCell.row, col: startCell.col };
  this.syncFormulaBarFromSelection({ row: startCell.row, col: startCell.col });

  const values = sheet?.getRangeData?.(
    startCell.row,
    startCell.col,
    result.endRow,
    result.endCol
  ) ?? matrix;

  const payload = {
    ok: true,
    status: `Wrote ${rangeRef}.`,
    startCell: startCell.ref,
    range: rangeRef,
    rowCount: result.rowCount,
    colCount: result.colCount,
    values
  };

  this.agentReadback = JSON.stringify(payload, null, 2);
  this.agentStatus = payload.status;
  return payload;
},

installHermesSheetBridge() {
  const bridge = {
    selectCell: (ref) => {
      this.agentCellRef = ref;
      return this.agentSelectCell();
    },
    readCell: (ref) => {
      this.agentCellRef = ref;
      return this.agentReadCell();
    },
    writeCell: (ref, value) => {
      this.agentCellRef = ref;
      this.agentCellValue = value == null ? '' : String(value);
      return this.agentWriteCell();
    },
    readRange: (ref) => {
      this.agentRangeRef = ref;
      return this.agentReadRange();
    },
    writeRange: (startRef, data) => {
      this.agentWriteRangeStartRef = startRef;
      this.agentWriteRangeData = typeof data === 'string'
        ? data
        : JSON.stringify(data, null, 2);
      return this.agentWriteRange();
    },
    resetSheet: () => {
      return this.performResetSheetReset();
    },
    getCaseSummary: () => {
      return this.refreshCaseSummary();
    },
    runCaseChecks: () => {
      return this.runWorkflowChecks();
    },
    openMainSheet: () => {
      return this.activateLogicalSheet('main');
    },
    openRawSheet: () => {
      return this.activateLogicalSheet('raw');
    },
    openRawDataImportModal: () => {
      this.openRawDataImportModal();
      return { ok: true, status: 'Raw data import modal opened.' };
    },
    analyzeRawDataCoverage: () => {
      return buildMainRawDataCoverage(this.getMatrixForLogicalSheet('main'), this.getMatrixForLogicalSheet('raw'));
    },
    openTool68PreparationModal: () => {
      this.openTool68PreparationModal();
      return { ok: true, status: 'Tool 68 sapling preparation modal opened.' };
    },
    analyzeTool68Preparation: () => {
      return buildSaplingPreparationAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.getMatrixForLogicalSheet('raw'),
        this.caseSupportState.workflowState || {}
      );
    },
    analyzePathOperations: () => {
      return buildPathOperationAnalysis(this.getMatrixForLogicalSheet('main'), this.getMatrixForLogicalSheet('raw'));
    },
    openPathOperationModal: () => {
      this.openPathOperationModal();
      return { ok: true, status: 'Path operation modal opened.' };
    },
    selectPathOperationRow: (rowRefOrNumber) => {
      const rowNumber = typeof rowRefOrNumber === 'number'
        ? rowRefOrNumber
        : this.parseCellRef(rowRefOrNumber)?.row + 1 || Number.parseInt(String(rowRefOrNumber || '').replace(/[^0-9]/g, ''), 10);
      if (!Number.isFinite(rowNumber) || rowNumber < 1) return { ok: false, status: 'Invalid row reference.' };
      this.refreshPathOperationCandidates({ silent: true, preserveSelection: true });
      const candidate = findPathOperationCandidateForRow({ candidates: this.pathOperationCandidates }, rowNumber - 1);
      if (!candidate) return { ok: false, status: `No qualified path operation candidate found for row ${rowNumber}.` };
      this.selectPathOperationCandidate(candidate.id);
      return { ok: true, status: `Selected path operation row ${rowNumber}.`, candidate };
    },
    openPathOperationForRow: (rowRefOrNumber, action = 'prune') => {
      const rowNumber = typeof rowRefOrNumber === 'number'
        ? rowRefOrNumber
        : this.parseCellRef(rowRefOrNumber)?.row + 1 || Number.parseInt(String(rowRefOrNumber || '').replace(/[^0-9]/g, ''), 10);
      if (!Number.isFinite(rowNumber) || rowNumber < 1) return { ok: false, status: 'Invalid row reference.' };
      const analysis = this.refreshPathOperationCandidates({ silent: true, preserveSelection: true });
      const candidate = findExactPathOperationCandidateForRow(analysis, rowNumber - 1);
      if (!candidate) return { ok: false, status: `No exact qualified path operation output found for row ${rowNumber}.` };
      const normalizedAction = ['prune', 'graft', 'bridge'].includes(action) ? action : 'prune';
      if (normalizedAction !== 'prune' && !candidate.isPathEnding) return { ok: false, status: 'Graft and Bridge graft require a current Path ends row.' };
      this.openPathOperationModal({ candidateId: candidate.id, action: normalizedAction, source: 'agent' });
      return { ok: true, status: `${this.pathOperationActionLabel} opened for row ${rowNumber}.`, candidate };
    },
    colorCaseAddressCells: () => {
      return this.applyCaseAddressColors();
    },
    applyAddressFlowColors: () => {
      return this.applyCaseAddressColors();
    },
    clearCaseAddressCellColors: () => {
      return this.clearCaseAddressColors();
    },
    clearAddressFlowColors: () => {
      return this.clearCaseAddressColors();
    },
    analyzeAddressFlowColors: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      const mainSheet = this.findLogicalSheetMeta('main');
      const state = mainSheet?.id ? this.getSheetStateById(mainSheet.id) : createCaseSheetState({ includeHeaders: true });
      return buildCaseAddressColorCoverageAnalysis(
        state,
        this.caseSupportState.workflowState || {},
        this.caseSupportState.caseDataRevision
      );
    },
    openEntityCuratorReviewModal: () => {
      this.openEntityCuratorReviewModal();
      return { ok: true, status: 'Known entity curator review opened.' };
    },
    analyzeKnownEntityCuratorReview: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.entityCuratorAnalysis;
    },
    refreshKnownEntityCurators: () => {
      return this.loadEntityCuratorRemoteKeywords({ force: true });
    },
    addLocalKnownEntity: (name, kind = 'exchange') => {
      const normalizedKind = String(kind || '').toLowerCase().includes('non') ? CASE_ENTITY_KIND_NON_EXCHANGE : CASE_ENTITY_KIND_EXCHANGE;
      const analysis = this.addLocalCuratorTerm(normalizedKind, name, { source: 'agent' });
      return { ok: !!analysis, status: analysis ? `Added ${name} to local ${formatCaseEntityKindLabel(normalizedKind)} curator.` : 'No term was added.', analysis };
    },
    suppressKnownEntityCandidate: (name) => {
      const analysis = this.suppressEntityCuratorTerm(name, { source: 'agent' });
      return { ok: !!analysis, status: analysis ? `Suppressed ${name} locally.` : 'No term was suppressed.', analysis };
    },
    openCaseInconsistencyReviewModal: () => {
      this.openCaseInconsistencyReviewModal();
      return { ok: true, status: 'Case inconsistency review opened.' };
    },
    analyzeCaseInconsistencies: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return buildCaseInconsistencyReviewAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.caseSupportState.workflowState.inconsistencyReview,
        this.caseInconsistencyRuleOptions
      );
    },
    applyCaseInconsistencySafeFixes: () => {
      return this.applyAllSafeCaseInconsistencyFixes();
    },
    markCaseInconsistencyReviewComplete: () => {
      return this.markCaseInconsistencyReviewComplete();
    },
    openDepositPatternReviewModal: () => {
      this.openDepositPatternReviewModal();
      return { ok: true, status: 'Deposit-pattern review modal opened.' };
    },
    analyzeDepositPatternReview: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return buildCaseDepositPatternReviewAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.caseSupportState.workflowState || {},
        { caseId: this.currentProjectFileId || this.caseDisplayId || 'case' }
      );
    },
    dismissRemainingDepositPatternCandidates: () => {
      return this.dismissRemainingDepositPatternCandidatesAndComplete();
    },
    markDepositPatternCandidateReviewed: (reviewKeyOrRowNumber, status = 'reviewed') => {
      const analysis = this.refreshDepositPatternReview({ silent: true });
      const rowNumber = Number.parseInt(String(reviewKeyOrRowNumber || '').replace(/[^0-9]/g, ''), 10);
      const candidate = (analysis.candidates || []).find(item => item.reviewKey === reviewKeyOrRowNumber || item.key === reviewKeyOrRowNumber)
        || (Number.isFinite(rowNumber) ? (analysis.candidates || []).find(item => item.childOutputRowNumber === rowNumber || item.childRowNumber === rowNumber) : null);
      if (!candidate) return { ok: false, status: 'Deposit-pattern candidate not found.' };
      const normalizedStatus = String(status || '').toLowerCase().includes('dismiss') ? CASE_DEPOSIT_PATTERN_REVIEW_STATUS_DISMISSED : CASE_DEPOSIT_PATTERN_REVIEW_STATUS_REVIEWED;
      const refreshed = this.saveDepositPatternDecisions([candidate], normalizedStatus, { source: 'agent' });
      return { ok: true, status: `Candidate row ${candidate.childOutputRowNumber || candidate.childRowNumber} marked ${normalizedStatus}.`, candidate, analysis: refreshed };
    },
    markDepositPatternReviewComplete: () => {
      return this.markDepositPatternReviewComplete();
    },
    openKnownEntityNoteCasingReviewModal: () => {
      this.openKnownEntityCasingReviewModal();
      return { ok: true, status: 'Known-entity note casing review modal opened.' };
    },
    analyzeKnownEntityNoteCasingReview: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return buildCaseKnownEntityCasingReviewAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        { caseId: this.currentProjectFileId || this.caseDisplayId || 'case' }
      );
    },
    applySelectedKnownEntityNoteCasingFixes: () => {
      this.openKnownEntityCasingReviewModal();
      return this.applySelectedKnownEntityCasingFixes();
    },
    dismissRemainingKnownEntityNoteCasingCandidates: () => {
      this.openKnownEntityCasingReviewModal();
      return this.dismissRemainingKnownEntityCasingCandidatesAndComplete();
    },
    markKnownEntityNoteCasingReviewComplete: () => {
      return this.markKnownEntityCasingReviewComplete();
    },
    openDuplicateHashLabelerModal: () => {
      this.openDuplicateHashLabelerModal();
      return { ok: true, status: 'Duplicate transaction hash labeler modal opened.' };
    },
    analyzeDuplicateTransactionHashes: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return buildCaseDuplicateHashLabelerAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.caseSupportState.workflowState || {},
        { caseId: this.currentProjectFileId || this.caseDisplayId || 'case' }
      );
    },
    analyzeDuplicateHashLabeler: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return buildCaseDuplicateHashLabelerAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.caseSupportState.workflowState || {},
        { caseId: this.currentProjectFileId || this.caseDisplayId || 'case' }
      );
    },
    applyDuplicateTransactionHashLabels: () => {
      this.openDuplicateHashLabelerModal();
      return this.applyAllEligibleDuplicateHashUpdates();
    },
    applyAllEligibleDuplicateHashUpdates: () => {
      this.openDuplicateHashLabelerModal();
      return this.applyAllEligibleDuplicateHashUpdates();
    },
    buildDuplicateHashTablePreview: () => {
      return this.buildDuplicateHashTablePreview();
    },
    markDuplicateTransactionHashLabelerComplete: () => {
      return this.markDuplicateHashLabelerReviewComplete();
    },
    markDuplicateHashLabelerReviewComplete: () => {
      return this.markDuplicateHashLabelerReviewComplete();
    },
    openNoteHashPathLabelRewriteModal: () => {
      this.openNoteHashRewriteModal();
      return { ok: true, status: 'Note hash/path-label rewrite modal opened.' };
    },
    analyzeNoteHashPathLabelRewrite: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return buildCaseNoteHashRewriteAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.caseSupportState.workflowState || {},
        { caseId: this.currentProjectFileId || this.caseDisplayId || 'case' }
      );
    },
    createOriginalNotesBackup: () => {
      this.openNoteHashRewriteModal();
      return this.createOrMergeOriginalNotesBackup({ replace: false });
    },
    stageOriginalNotesBackupPreview: () => {
      return this.stageOriginalNotesBackupPreview();
    },
    applyNoteHashPathLabelRewrites: () => {
      this.openNoteHashRewriteModal();
      return this.applyAllEligibleNoteHashRewrites();
    },
    markNoteHashPathLabelRewriteComplete: () => {
      return this.markNoteHashRewriteReviewComplete(null, { source: 'agent-bridge' });
    },
    openDepositHotWalletNoteNormalizer: () => {
      this.openDepositHotWalletModal();
      return { ok: true, status: 'Deposit / hot-wallet note normalization modal opened.' };
    },
    analyzeDepositHotWalletNotes: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return buildCaseDepositHotWalletReviewAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        this.depositHotWalletVendorResultsByKey
      );
    },
    applyDepositHotWalletSafeNormalizations: () => {
      this.openDepositHotWalletModal();
      return this.applySelectedDepositHotWalletSafeNormalizations();
    },
    runDepositHotWalletVendorLookup: () => {
      this.openDepositHotWalletModal();
      return this.runDepositHotWalletVendorLookup();
    },
    markDepositHotWalletReviewComplete: () => {
      return this.markDepositHotWalletReviewComplete();
    },
    openAddressLegendModal: () => {
      this.openAddressLegendModal();
      return { ok: true, status: 'Address legend review modal opened.' };
    },
    openAddressLegendReviewModal: () => {
      this.openAddressLegendModal();
      return { ok: true, status: 'Address legend review modal opened.' };
    },
    analyzeAddressLegend: async () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.refreshAddressLegendReview({ silent: true });
    },
    buildAddressLegend: async () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.buildCurrentAddressLegend({ source: 'agent-bridge' });
    },
    rebuildAddressLegend: async () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.buildCurrentAddressLegend({ source: 'agent-bridge' });
    },
    stageAddressLegendPreview: async () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.stageAddressLegendPreview({ source: 'agent-bridge', silent: true });
    },
    markAddressLegendReviewComplete: async () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.markAddressLegendReviewComplete({ source: 'agent-bridge' });
    },
    analyzeCaseTemplateDeliverable: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.getCaseTemplateState();
    },
    openTemplateDeliverableReviewModal: () => {
      this.openTemplateDeliverableModal();
      return { ok: true, status: 'Template deliverable review modal opened.' };
    },
    stageTemplateDeliverablePreview: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.stageTemplateDeliverablePreview({ source: 'agent-bridge', silent: true });
    },
    downloadTemplateDeliverable: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.downloadTemplateDeliverableTsv({ source: 'agent-bridge', silent: true });
    },
    markTemplateDeliverableReviewComplete: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.markTemplateDeliverableReviewComplete({ source: 'agent-bridge' });
    },
    buildCaseTemplateDeliverable: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      const definition = CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(deliverable => deliverable.id === 'template');
      const view = definition ? this.buildDeliverableView(definition) : null;
      if (!view?.canBuild) return { ok: false, status: view?.requirementLabel || 'Template deliverable is not ready to build.', state: this.getCaseTemplateState() };
      this.buildDeliverable(view);
      return { ok: true, status: 'Template deliverable preview staged in Output.', state: this.getCaseTemplateState() };
    },

    analyzeSuspiciousAddressTable: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.getSuspiciousAddressTableState();
    },
    openSuspiciousAddressTableReviewModal: () => {
      this.openSuspiciousAddressTableModal();
      return { ok: true, status: 'Suspicious address table review modal opened.' };
    },
    stageSuspiciousAddressTablePreview: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.stageSuspiciousAddressTablePreview({ source: 'agent-bridge', silent: true });
    },
    downloadSuspiciousAddressTable: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.downloadSuspiciousAddressTableTsv({ source: 'agent-bridge', silent: true });
    },
    markSuspiciousAddressTableReviewComplete: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.markSuspiciousAddressTableReviewComplete({ source: 'agent-bridge' });
    },
    buildSuspiciousAddressTableDeliverable: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      const definition = CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(deliverable => deliverable.id === 'suspicious_address_table');
      const view = definition ? this.buildDeliverableView(definition) : null;
      if (!view?.canBuild) return { ok: false, status: view?.requirementLabel || 'Suspicious address table is not ready to build.', state: this.getSuspiciousAddressTableState() };
      this.buildDeliverable(view);
      return { ok: true, status: 'Suspicious address table preview staged in Output.', state: this.getSuspiciousAddressTableState() };
    },
    analyzeExchangeAddressTable: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.getExchangeAddressTableState();
    },
    openExchangeAddressTableReviewModal: () => {
      this.openExchangeAddressTableModal();
      return { ok: true, status: 'Exchange address table review modal opened.' };
    },
    stageExchangeAddressTablePreview: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.stageExchangeAddressTablePreview({ source: 'agent-bridge', silent: true });
    },
    downloadExchangeAddressTable: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.downloadExchangeAddressTableTsv({ source: 'agent-bridge', silent: true });
    },
    markExchangeAddressTableReviewComplete: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.markExchangeAddressTableReviewComplete({ source: 'agent-bridge' });
    },
    buildExchangeAddressTableDeliverable: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      const definition = CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(deliverable => deliverable.id === 'exchange_address_table');
      const view = definition ? this.buildDeliverableView(definition) : null;
      if (!view?.canBuild) return { ok: false, status: view?.requirementLabel || 'Exchange address table is not ready to build.', state: this.getExchangeAddressTableState() };
      this.buildDeliverable(view);
      return { ok: true, status: 'Exchange address table preview staged in Output.', state: this.getExchangeAddressTableState() };
    },
    analyzeDormantAddressDeliverables: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.getDormantAddressDeliverablesState();
    },
    openDormantAddressDeliverablesReviewModal: () => {
      this.openDormantAddressDeliverablesModal();
      return { ok: true, status: 'Dormant address deliverables review modal opened.' };
    },
    stageDormantAddressTablePreview: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.stageDormantAddressTablePreview({ source: 'agent-bridge', silent: true });
    },
    stageDormantAddressMonitoringPreview: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.stageDormantAddressMonitoringPreview({ source: 'agent-bridge', silent: true });
    },
    downloadDormantAddressTable: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.downloadDormantAddressTableTsv({ source: 'agent-bridge', silent: true });
    },
    downloadDormantAddressMonitoringView: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.downloadDormantAddressMonitoringTsv({ source: 'agent-bridge', silent: true });
    },
    markDormantAddressDeliverablesReviewComplete: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.markDormantAddressDeliverablesReviewComplete({ source: 'agent-bridge' });
    },
    buildDormantAddressDeliverables: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      const definition = CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(deliverable => deliverable.id === 'dormant_address_table');
      const view = definition ? this.buildDeliverableView(definition) : null;
      if (!view?.canBuild) return { ok: false, status: view?.requirementLabel || 'Dormant address deliverables are not ready to build.', state: this.getDormantAddressDeliverablesState() };
      this.stageDormantAddressTablePreview({ source: 'agent-bridge', silent: true });
      this.stageDormantAddressMonitoringPreview({ source: 'agent-bridge', silent: true });
      return { ok: true, status: 'Dormant address deliverables staged in Output; last staged view is monitoring.', state: this.getDormantAddressDeliverablesState() };
    },
    analyzeWhatWentWhere: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.getWhatWentWhereState();
    },
    openWhatWentWhereReviewModal: () => {
      this.openWhatWentWhereModal();
      return { ok: true, status: 'What Went Where review modal opened.' };
    },
    estimateWhatWentWhereUsdReferences: (selectedOnly = false) => {
      this.openWhatWentWhereModal();
      return this.estimateWhatWentWhereUsdReferences({ selectedOnly: !!selectedOnly });
    },
    stageWhatWentWherePreview: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.stageWhatWentWherePreview({ source: 'agent-bridge', silent: true });
    },
    stageWhatWentWhereValuationAudit: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.stageWhatWentWhereValuationAudit({ source: 'agent-bridge', silent: true });
    },
    downloadWhatWentWhere: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.downloadWhatWentWhereTsv({ source: 'agent-bridge', silent: true });
    },
    markWhatWentWhereReviewComplete: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.markWhatWentWhereReviewComplete({ source: 'agent-bridge' });
    },
    buildWhatWentWhereDeliverable: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      const definition = CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(deliverable => deliverable.id === 'what_went_where');
      const view = definition ? this.buildDeliverableView(definition) : null;
      if (!view?.canBuild) return { ok: false, status: view?.requirementLabel || 'What Went Where is not ready to build.', state: this.getWhatWentWhereState() };
      this.stageWhatWentWherePreview({ source: 'agent-bridge', silent: true });
      return { ok: true, status: 'What Went Where preview staged in Output.', state: this.getWhatWentWhereState() };
    },
    analyzeCaseConclusion: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.getCaseConclusionState();
    },
    openCaseConclusionReviewModal: () => {
      this.openCaseConclusionModal();
      return { ok: true, status: 'Case conclusion review modal opened.' };
    },
    stageCaseConclusionPreview: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.stageCaseConclusionPreview({ source: 'agent-bridge', silent: true });
    },
    copyCaseConclusionText: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.copyCaseConclusionText();
    },
    downloadCaseConclusionText: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.downloadCaseConclusionText({ source: 'agent-bridge', silent: true });
    },
    markCaseConclusionReviewComplete: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.markCaseConclusionReviewComplete({ source: 'agent-bridge' });
    },
    buildCaseConclusionDeliverable: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      const state = this.getCaseConclusionState();
      if (!state.canBuild) return { ok: false, status: state.summary || 'Case conclusion is not ready.', state };
      this.stageCaseConclusionPreview({ source: 'agent-bridge', silent: true });
      return { ok: true, status: 'Case conclusion preview staged in Output.', state: this.getCaseConclusionState() };
    },
    analyzeReportBody: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.getReportBodyState();
    },
    openReportBodyReviewModal: () => {
      this.openReportBodyModal();
      return { ok: true, status: 'Report body review modal opened.' };
    },
    stageReportBodyPreview: (variantId = CASE_REPORT_BODY_VARIANT_STANDARD) => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.stageReportBodyPreview({ variantId, source: 'agent-bridge', silent: true });
    },
    copyReportBodyRich: (variantId = CASE_REPORT_BODY_VARIANT_STANDARD) => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.copyReportBodyRich({ variantId, source: 'agent-bridge' });
    },
    copyReportBodyText: (variantId = CASE_REPORT_BODY_VARIANT_STANDARD) => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.copyReportBodyText({ variantId, source: 'agent-bridge' });
    },
    downloadReportBodyText: (variantId = CASE_REPORT_BODY_VARIANT_STANDARD) => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.downloadReportBodyText({ variantId, source: 'agent-bridge', silent: true });
    },
    markReportBodyReviewComplete: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      return this.markReportBodyReviewComplete({ source: 'agent-bridge' });
    },
    buildReportBodyDeliverable: () => {
      this.ensureCaseWorkbookScaffold({ silent: true });
      const definition = CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(deliverable => deliverable.id === 'report_body');
      const view = definition ? this.buildDeliverableView(definition) : null;
      if (!view?.canBuild) return { ok: false, status: view?.requirementLabel || 'Report body is not ready to build.', state: this.getReportBodyState() };
      this.stageReportBodyPreview({ variantId: CASE_REPORT_BODY_VARIANT_STANDARD, source: 'agent-bridge', silent: true });
      return { ok: true, status: 'Report body preview staged in Output.', state: this.getReportBodyState() };
    },
    openStartingMainTransactionsModal: () => {
      this.openStartingMainTransactionsModal();
      return { ok: true, status: 'Starting main transaction loader opened.' };
    },
    openNetworkDiscoveryModal: () => {
      this.openNetworkLookupModal();
      return { ok: true, status: 'Network discovery opened.' };
    },
    runNetworkDiscovery: () => {
      this.openNetworkLookupModal();
      return this.runNetworkDiscoveryLookup();
    }
  };

  Object.defineProperty(bridge, '__owner', {
    value: this,
    enumerable: false
  });

  window.hermesSheetBridge = bridge;
},

columnLabelToIndex(label) {
  const normalized = String(label || '').trim().toUpperCase();
  if (!/^[A-Z]+$/.test(normalized)) return -1;

  let value = 0;
  for (let i = 0; i < normalized.length; i++) {
    value = (value * 26) + (normalized.charCodeAt(i) - 64);
  }
  return value - 1;
},

indexToColumnLabel(index) {
  let value = Number(index);
  if (!Number.isInteger(value) || value < 0) return '';

  let label = '';
  while (value >= 0) {
    label = String.fromCharCode((value % 26) + 65) + label;
    value = Math.floor(value / 26) - 1;
  }
  return label;
},

parseCellRef(ref) {
  const match = String(ref || '').trim().toUpperCase().match(/^([A-Z]+)(\d+)$/);
  if (!match) return null;

  const col = this.columnLabelToIndex(match[1]);
  const row = parseInt(match[2], 10) - 1;

  if (col < 0 || row < 0) return null;

  return { row, col, ref: `${match[1]}${match[2]}` };
},

parseRangeRef(ref) {
  const text = String(ref || '').trim().toUpperCase();
  const parts = text.split(':').map(part => part.trim()).filter(Boolean);

  if (parts.length === 0 || parts.length > 2) return null;

  const start = this.parseCellRef(parts[0]);
  const end = this.parseCellRef(parts[1] || parts[0]);

  if (!start || !end) return null;

  return {
    startRow: Math.min(start.row, end.row),
    endRow: Math.max(start.row, end.row),
    startCol: Math.min(start.col, end.col),
    endCol: Math.max(start.col, end.col)
  };
},

agentSelectCell() {
  this.agentReadback = '';

  const cell = this.parseCellRef(this.agentCellRef);
  if (!cell) {
    this.agentStatus = 'Invalid cell reference.';
    return { ok: false, status: this.agentStatus };
  }

  const sheet = this.getInputSheet();
  if (!sheet?.selectCell?.(cell.row, cell.col)) {
    this.agentStatus = `Failed to select ${cell.ref}.`;
    return { ok: false, status: this.agentStatus, cell: cell.ref };
  }

  this.formulaTargetCell = { row: cell.row, col: cell.col };
  this.syncFormulaBarFromSelection({ row: cell.row, col: cell.col });
  this.agentStatus = `Selected ${cell.ref}.`;

  return {
    ok: true,
    status: this.agentStatus,
    cell: cell.ref
  };
},

agentReadCell() {
  this.agentReadback = '';

  const cell = this.parseCellRef(this.agentCellRef);
  if (!cell) {
    this.agentStatus = 'Invalid cell reference.';
    return { ok: false, status: this.agentStatus };
  }

  const sheet = this.getInputSheet();
  const value = sheet?.getCellInput?.(cell.row, cell.col) ?? '';
  const displayValue = this.getInputHot()?.getDataAtCell?.(cell.row, cell.col) ?? '';

  const payload = {
    ok: true,
    status: `Read ${cell.ref}.`,
    cell: cell.ref,
    value,
    displayValue
  };

  this.agentReadback = JSON.stringify(payload, null, 2);
  this.agentStatus = payload.status;
  return payload;
},

agentWriteCell() {
  this.agentReadback = '';

  const cell = this.parseCellRef(this.agentCellRef);
  if (!cell) {
    this.agentStatus = 'Invalid cell reference.';
    return { ok: false, status: this.agentStatus };
  }

  const sheet = this.getInputSheet();
  const ok = sheet?.setCellInput?.(cell.row, cell.col, this.agentCellValue, 'agent_panel');

  if (!ok) {
    this.agentStatus = `Failed to write ${cell.ref}.`;
    return { ok: false, status: this.agentStatus, cell: cell.ref };
  }

  sheet?.selectCell?.(cell.row, cell.col);
  this.formulaTargetCell = { row: cell.row, col: cell.col };
  this.syncFormulaBarFromSelection({ row: cell.row, col: cell.col });

  const value = sheet?.getCellInput?.(cell.row, cell.col) ?? '';
  const displayValue = this.getInputHot()?.getDataAtCell?.(cell.row, cell.col) ?? '';

  const payload = {
    ok: true,
    status: `Wrote ${cell.ref}.`,
    cell: cell.ref,
    value,
    displayValue
  };

  this.agentReadback = JSON.stringify(payload, null, 2);
  this.agentStatus = payload.status;
  return payload;
},

agentClearCell() {
  this.agentCellValue = '';
  this.agentWriteCell();
},

agentReadRange() {
  this.agentReadback = '';

  const range = this.parseRangeRef(this.agentRangeRef);
  if (!range) {
    this.agentStatus = 'Invalid range reference.';
    return { ok: false, status: this.agentStatus };
  }

  const hot = this.getInputHot();
  if (!hot) {
    this.agentStatus = 'Spreadsheet is not ready.';
    return { ok: false, status: this.agentStatus };
  }

  const rows = [];
  for (let row = range.startRow; row <= range.endRow; row++) {
    const rowValues = [];
    for (let col = range.startCol; col <= range.endCol; col++) {
      rowValues.push(hot.getDataAtCell(row, col) ?? '');
    }
    rows.push(rowValues);
  }

  const payload = {
    ok: true,
    status: `Read ${this.agentRangeRef.toUpperCase()}.`,
    range: this.agentRangeRef.toUpperCase(),
    values: rows
  };

  this.agentReadback = JSON.stringify(payload, null, 2);
  this.agentStatus = payload.status;
  return payload;
}
};
